
import { useState } from 'react';
import { Button } from '../../../components/base/Button';

export const InvestmentSection = () => {
  const [selectedPackage, setSelectedPackage] = useState(1);
  const [investmentAmount, setInvestmentAmount] = useState(10000);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    investment_amount: 10000,
    investment_package: 'Growth Investor',
    message: ''
  });

  const packages = [
    {
      id: 0,
      title: 'Early Bird',
      minAmount: 1000,
      equity: '0.01%',
      benefits: ['제품 우선 구매권', '분기별 리포트', '온라인 투자자 미팅'],
      color: 'border-gray-200'
    },
    {
      id: 1,
      title: 'Growth Investor',
      minAmount: 10000,
      equity: '0.1%',
      benefits: ['제품 우선 구매권', '월간 리포트', '오프라인 투자자 미팅', '제품 개발 참여'],
      color: 'border-blue-500 bg-blue-50',
      popular: true
    },
    {
      id: 2,
      title: 'Strategic Partner',
      minAmount: 50000,
      equity: '0.5%',
      benefits: ['제품 우선 구매권', '주간 리포트', '경영진과 직접 미팅', '제품 개발 참여', '이사회 참관'],
      color: 'border-purple-500'
    },
    {
      id: 3,
      title: 'Major Investor',
      minAmount: 100000,
      equity: '1.0%',
      benefits: ['모든 혜택', '이사회 의결권', '경영 자문 참여', '우선 매수권', 'IPO 우선권'],
      color: 'border-gold-500'
    }
  ];

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formDataToSubmit = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      formDataToSubmit.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d46h60njsic9gsa4u4sg', {
        method: 'POST',
        body: formDataToSubmit
      });

      if (response.ok) {
        alert('투자 신청이 성공적으로 접수되었습니다. 담당자가 곧 연락드리겠습니다.');
        setIsFormVisible(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          investment_amount: 10000,
          investment_package: 'Growth Investor',
          message: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      alert('투자 신청 중 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            투자 패키지 선택
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            다양한 투자 옵션 중에서 귀하에게 적합한 패키지를 선택하세요.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {packages.map((pkg) => (
            <div 
              key={pkg.id}
              className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg ${
                selectedPackage === pkg.id ? pkg.color : 'border-gray-200 bg-white'
              }`}
              onClick={() => {
                setSelectedPackage(pkg.id);
                setInvestmentAmount(pkg.minAmount);
                setFormData(prev => ({
                  ...prev,
                  investment_amount: pkg.minAmount,
                  investment_package: pkg.title
                }));
              }}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    인기
                  </span>
                </div>
              )}
              
              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.title}</h3>
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  ${pkg.minAmount.toLocaleString()}+
                </div>
                <div className="text-sm text-gray-600">지분 {pkg.equity}</div>
              </div>
              
              <ul className="space-y-2 mb-6">
                {pkg.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start text-sm text-gray-700">
                    <i className="ri-check-line text-green-600 mr-2 mt-0.5 flex-shrink-0"></i>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-white p-8 rounded-xl shadow-lg mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">투자 금액 설정</h3>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                투자 금액 (USD)
              </label>
              <input
                type="number"
                min={packages[selectedPackage].minAmount}
                step="1000"
                value={investmentAmount}
                onChange={(e) => {
                  const amount = parseInt(e.target.value);
                  setInvestmentAmount(amount);
                  setFormData(prev => ({
                    ...prev,
                    investment_amount: amount
                  }));
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-2xl font-bold"
              />
              <p className="text-sm text-gray-500 mt-2">
                최소 투자금액: ${packages[selectedPackage].minAmount.toLocaleString()}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-lg font-bold text-blue-600">
                  {((investmentAmount / 2500000) * 100).toFixed(3)}%
                </div>
                <div className="text-sm text-gray-600">예상 지분</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-lg font-bold text-green-600">
                  ${(investmentAmount * 0.3).toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">3년 예상 수익</div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <div className="text-lg font-bold text-purple-600">30%</div>
                <div className="text-sm text-gray-600">연간 예상 ROI</div>
              </div>
            </div>

            <Button 
              variant="primary" 
              size="lg" 
              className="w-full text-lg py-4"
              onClick={() => setIsFormVisible(true)}
            >
              <i className="ri-funds-line mr-2"></i>
              투자 신청하기
            </Button>
          </div>
        </div>

        {/* 투자 신청 폼 */}
        {isFormVisible && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">투자 신청서</h3>
                  <button 
                    onClick={() => setIsFormVisible(false)}
                    className="text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>

                <form onSubmit={handleFormSubmit} data-readdy-form id="crowdfunding-investment">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        성명 *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        이메일 *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        연락처 *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        회사/기관명
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        투자 금액 (USD)
                      </label>
                      <input
                        type="number"
                        name="investment_amount"
                        value={formData.investment_amount}
                        onChange={handleInputChange}
                        readonly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        투자 패키지
                      </label>
                      <input
                        type="text"
                        name="investment_package"
                        value={formData.investment_package}
                        onChange={handleInputChange}
                        readonly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        추가 메시지
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        maxLength={500}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        placeholder="투자 관련 문의사항이나 요청사항을 적어주세요."
                      ></textarea>
                      <div className="text-right text-xs text-gray-500 mt-1">
                        {formData.message.length}/500
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setIsFormVisible(false)}
                    >
                      취소
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      className="flex-1"
                    >
                      신청하기
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
